/*
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2025 Pearson Education, Inc.
 * All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Pearson Education, Inc. The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */

/**
 * Assignment Get Service
 * This service is used to get list of StudentsRoster and Students by courseId and studentId
 * @file StudentsRosterService.js
 * @author Mohamed yasar arabath M
 */

import Framework, { BaseService } from '@greenville/framework';
import { AET_STORE_EVENTS } from '../common/Constants';
import CommonUtils from '../../../common/utils/CommonUtils';

export default class StudentsRosterService extends BaseService {
  constructor() {
    super(AET_STORE_EVENTS.STUDENTS_ROSTER_REQUESTED, 'studentsRosterStatus');
  }

  /**
   * API call for Assignment get
   * @param {*} eventName
   * @param {*} eventData
   * @returns {Promise}
   */
  handleEvent(eventName, eventData = {}) {
    const {
      courseId,
      isRoster = true,
      studentId = null,
      includeAllUsers = false
    } = eventData;
    const studentsRosterStore = Framework.getStoreRegistry().getStore('studentsRoster');

    let endpoint = '';
    if (isRoster && studentId) {
      endpoint = `/progressSummary?studentId=${studentId}`;
    } else if (isRoster) {
      studentsRosterStore.setIsPending(true);
      endpoint = '/progressSummary';
    }

    const client = isRoster ? this.getClient('marinV2') : this.getClient('marin');
    const query = includeAllUsers ? '?includeAllUsers=true' : '';

    return client.get(`/aet/courses/${courseId}/students${endpoint}${query}`);
  }

  // eslint-disable-next-line class-methods-use-this
  handleResponse(response, eventData = {}) {
    const {
      courseId,
      isRoster = true,
      studentId = null,
      includeAllUsers = false
    } = eventData;
    const studentsRosterStore = Framework.getStoreRegistry().getStore('studentsRoster');
    const studentsStore = Framework.getStoreRegistry().getStore('students');
    const assignmentStore = Framework.getStoreRegistry().getStore('assignment');
    const studentsRosterData = response?.data || { students: [], totalAssignments: 0 };
    const studentsData = response?.data || { students: [] };

    if (isRoster) {
      studentsRosterStore.setStudentRoster(studentsRosterData, courseId);
      studentsRosterStore.setIsPending(false);
      Framework.getEventManager().publish(AET_STORE_EVENTS.STUDENTS_ROSTER_COMPLETED, studentsRosterData);
    } else if (includeAllUsers) {
      studentsStore.setAllUsers(studentsData);

      if (CommonUtils.isHeroAetCourse(courseId)) {
        studentsRosterStore.syncWithActiveStudents(studentsStore);
      }

      Framework.getEventManager().publish(AET_STORE_EVENTS.STUDENTS_COMPLETED, studentsData);
    } else {
      studentsStore.setStudents(studentsData);

      if (CommonUtils.isHeroAetCourse(courseId)) {
        studentsRosterStore.syncWithActiveStudents(studentsStore);
      }

      Framework.getEventManager().publish(AET_STORE_EVENTS.STUDENTS_COMPLETED, studentsData);
    }

    if (studentId) {
      assignmentStore.setStudentPerformanceOfAssignment(
        studentsRosterData
      );
    }
  }

  // eslint-disable-next-line class-methods-use-this
  handleError(error, eventData = {}) {
    const { isRoster = true } = eventData;
    const studentsRosterStore = Framework.getStoreRegistry().getStore('studentsRoster');
    if (isRoster) {
      studentsRosterStore.setProgressSummaryErrorStatus(error?.response?.status || null);
      studentsRosterStore.setIsPending(false);
      studentsRosterStore.setIsError(true);
    } else {
      studentsRosterStore.setIsStudentListFetchError(true);
      studentsRosterStore.setStudentListErrorStatus(error?.response?.status || null);
    }
    Framework.getEventManager().publish(AET_STORE_EVENTS.STUDENTS_ROSTER_FAILED, error);
  }
}
